import React, { useState } from 'react';
import { Plus, Search, Filter, Edit2, Shield, AlertCircle, X } from 'lucide-react';
import { useFieldsByObjectPaginated, getFieldTypeLabel, type CustomField } from '../../../services/queries/fieldQueries';
import type { CustomObject } from '../../../services/queries/objectQueries';
import { CreateFieldModal } from './CreateFieldModal';
import { EditFieldModal } from './EditFieldModal';
import { DevModeBanner } from '../../common/DevModeBanner';
import { FieldSecurityManager } from '../object-manager/FieldSecurityManager';
import { Pagination } from '../../lists/Pagination';

interface FieldsTabProps {
  objectId: string;
  object: CustomObject;
}

/**
 * Fields Tab
 *
 * Manages custom fields and relationships for a specific object.
 * Displays both standard and custom fields with search and type filtering.
 * Users can create new fields, edit existing ones, and view field properties like data type,
 * required status, unique constraints, and PII designation.
 *
 * Data Source: fieldQueries.ts (React Query with mock data)
 *
 * @component
 */
export const FieldsTab: React.FC<FieldsTabProps> = ({ objectId, object }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [permissionsField, setPermissionsField] = useState<CustomField | null>(null);
  
  // Pagination state (UI uses 1-indexed pages, API uses 0-indexed)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    data,
    isLoading,
    error
  } = useFieldsByObjectPaginated(objectId, {
    page: currentPage - 1, // Convert to 0-indexed for API
    pageSize,
    filters: {
      searchQuery,
      typeFilter: typeFilter !== 'all' ? typeFilter : undefined,
      includeInactive: false
    }
  });

  const fields = data?.fields || [];
  const totalCount = data?.totalCount || 0;
  const hasMore = data?.hasMore || false;
  
  // Calculate total pages (estimate if hasMore is true)
  const totalPages = hasMore 
    ? Math.ceil((totalCount || (currentPage * pageSize + 1)) / pageSize)
    : Math.ceil(totalCount / pageSize) || 1;

  // Separate standard and custom fields
  const standardFields = fields.filter(f => f.isStandard);
  const customFields = fields.filter(f => !f.isStandard);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Fields & Relationships</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage custom fields and relationships for {object.label} records
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <Plus className="w-4 h-4" />
            New Field
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="date">Date</option>
              <option value="picklist">Picklist</option>
              <option value="lookup">Lookup</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
            </select>
          </div>
        </div>
      </div>

      {/* Development Mode Banner */}
      <DevModeBanner
        dataSource="mockDataManager.ts"
        itemCount={`${totalCount}${hasMore ? '+' : ''} field types`}
        customMessage={`Development Mode: Showing ${fields.length} of ${totalCount}${hasMore ? '+' : ''} fields persisted via mockDataManager.ts (localStorage). Changes survive page refresh.`}
        dismissible={true}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading fields...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900 dark:text-red-100">Error loading fields</p>
            <p className="text-sm text-red-800 dark:text-red-300 mt-1">Please try again or contact support if the problem persists.</p>
          </div>
        </div>
      )}

      {/* Fields List */}
      {!isLoading && !error && (
        <>
          {/* Results Count */}
          <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Showing {fields.length} field{fields.length !== 1 ? 's' : ''} on page {currentPage} of {totalPages}
            {' '}({standardFields.length} standard, {customFields.length} custom)
            {totalCount > 0 && (
              <span className="ml-2 text-gray-500 dark:text-gray-500">
                • {totalCount}{hasMore ? '+' : ''} total
              </span>
            )}
          </div>

          {/* Standard Fields */}
          {standardFields.length > 0 && (
            <div className="mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Standard Fields</h3>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {standardFields.map((field) => (
                    <FieldRow
                      key={field.id}
                      field={field}
                      onEdit={() => setEditingField(field)}
                      onSetPermissions={() => setPermissionsField(field)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Custom Fields */}
          {customFields.length > 0 && (
            <div className="mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Custom Fields</h3>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {customFields.map((field) => (
                    <FieldRow
                      key={field.id}
                      field={field}
                      onEdit={() => setEditingField(field)}
                      onSetPermissions={() => setPermissionsField(field)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalRecords={totalCount}
                onPageChange={setCurrentPage}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setCurrentPage(1); // Reset to first page when page size changes
                }}
              />
            </div>
          )}

          {/* Empty State */}
          {fields.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No fields found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchQuery || typeFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Get started by creating your first custom field'
                }
              </p>
              {(searchQuery || typeFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setTypeFilter('all');
                  }}
                  className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium focus:outline-none focus:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Field Modal */}
      <CreateFieldModal
        objectId={objectId}
        objectLabel={object.label}
        objectName={object.apiName}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Edit Field Modal */}
      <EditFieldModal
        field={editingField}
        isOpen={!!editingField}
        onClose={() => setEditingField(null)}
        objectId={objectId}
      />

      {/* Field Permissions Modal */}
      {permissionsField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Set Field Permissions</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {permissionsField.fieldLabel} • {object.label}
                </p>
              </div>
              <button
                onClick={() => setPermissionsField(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <FieldSecurityManager
                fieldId={permissionsField.id}
                fieldLabel={permissionsField.fieldLabel}
                objectName={object.apiName}
                mode="full"
              />
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end bg-gray-50 dark:bg-gray-800">
              <button
                onClick={() => setPermissionsField(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Field Row Component
interface FieldRowProps {
  field: CustomField;
  onEdit: () => void;
  onSetPermissions: () => void;
}

const FieldRow: React.FC<FieldRowProps> = ({ field, onEdit, onSetPermissions }) => (
  <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-medium text-gray-900 dark:text-gray-100">{field.fieldLabel}</span>
          {field.required && (
            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded font-medium">
              Required
            </span>
          )}
          {field.unique && (
            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded font-medium">
              Unique
            </span>
          )}
          {field.isPII && (
            <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs rounded font-medium flex items-center gap-1">
              <Shield className="w-3 h-3" />
              PII
            </span>
          )}
          {field.isStandard && (
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded font-medium">
              Standard
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 flex-wrap">
          <span className="font-mono">{field.fieldName}</span>
          <span>•</span>
          <span>{getFieldTypeLabel(field.fieldType)}</span>
          {field.description && (
            <>
              <span>•</span>
              <span className="truncate max-w-md">{field.description}</span>
            </>
          )}
        </div>
      </div>
      <div className="ml-4 flex items-center gap-2">
        <button
          onClick={onSetPermissions}
          className="flex items-center gap-2 px-3 py-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
          title="Set Field Permissions"
        >
          <Shield className="w-4 h-4" />
          <span className="text-sm font-medium hidden sm:inline">Permissions</span>
        </button>
        <button
          onClick={onEdit}
          className="flex items-center gap-2 px-3 py-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <Edit2 className="w-4 h-4" />
          <span className="text-sm font-medium">Edit</span>
        </button>
      </div>
    </div>
  </div>
);
